import { Store, Actions } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { CameraService } from '../engine/camera.service';
import { EngineService } from '../engine/engine.service';
import { PaintService } from '../engine/paint.service';
import { defineProcedure } from './define-procedure';
import { getCameraProcedures, getCameraEvents } from './procedures/camera';
import { getSceneProcedures, getSceneEvents } from './procedures/scene';
import { defineEvent } from './define-event';
import { getHumanEvents, getHumanProcedures } from './procedures/human';
import { HumanInterface } from './models/human.interface';
import { WebGLInfoModel } from './models/webgl-info.model';
import { getTimelineEvents, getTimelineProcedures } from './procedures/timeline';
import { AnimationService } from '../engine/animation.service';
import { getInputEvents, getInputProcedures } from './procedures/input';
import { getUIEvents, getUIProcedures } from './procedures/ui';
import { getLabelsEvents, getLabelsProcedures } from './procedures/labels';
import { LabelsService } from '../story/labels.service';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { ViewService } from '../view/view.service';
import { uniqBy } from 'lodash';
import { WidgetPlatformEnum } from '../widget-auth/models/widget-platform.enum';
import { WidgetInteractionEnum } from '../widget-auth/models/widget-interaction.enum';
import { WidgetAnalyticsService } from '../widget-auth/widget-analytics.service';
import { VisibilityLayersService } from 'src/app/story/visibility-layers.service';
import { WidgetState } from '../widget-auth/state/widget.state';
import { EventsEnum } from '../engine/models/events.enum';
import { getQuizEvents, getQuizProcedures } from './procedures/quiz';
import { QuizService } from '../view/components/view-quiz/view-quiz.service';

declare const Human: HumanInterface;
declare const SceneJS: any;

@Injectable({
  providedIn: 'root'
})
export class WidgetAPIHandler {

  private webGLInfo: WebGLInfoModel;

  constructor(
    private store: Store,
    private actions: Actions,
    private cameraService: CameraService,
    private engineService: EngineService,
    private animationService: AnimationService,
    private paintService: PaintService,
    private labelsService: LabelsService,
    private graphService: BdGraphService,
    private viewService: ViewService,
    private widgetAnalyticsService: WidgetAnalyticsService,
    private visibilityLayersService: VisibilityLayersService,
    private quizService: QuizService
  ) {
    this.webGLInfo = SceneJS.WEBGL_INFO;
  }

  listen(enableAnalytics: boolean) {
    this.initProcedures();
    this.initEvents();
   
    if (enableAnalytics) {
      this.logAPIUsage();
    }
  }

  private logAPIUsage() {
    const sendInterval = 10000;
    setInterval(() => {
      // get the RPC log
      const log = Human.rpc.getLog();
      const uniqueEntries = uniqBy(log, (entry) => entry['identifier']);
    
      if (uniqueEntries.length > 0) {
        const activityData = JSON.stringify(uniqueEntries);
        const widgetState = this.store.selectSnapshot(WidgetState.getWidgetState);
        const payload = this.widgetAnalyticsService.buildDeveloperAnalytic(
          widgetState,
          WidgetInteractionEnum.Call,
          WidgetPlatformEnum.WidgetAPI,
          activityData
        );
    
        // the http request is not fired unless the observable is subscribed to ?
        this.widgetAnalyticsService.sendAnalytic(payload).subscribe({
          next: (e) => {},
          error: (e) => {
            throw new Error(e);
          },
        });
    
        // reset the log
        Human.rpc.resetLog();
      }
    }, sendInterval);
  }

  private initEvents() {


    defineEvent(
      EventsEnum.HumanProgress,
      undefined,
      (e) => e
    );

    getHumanEvents().forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getSceneEvents().forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getCameraEvents(this.cameraService).forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getTimelineEvents().forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getInputEvents().forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getUIEvents().forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getLabelsEvents(Human, this.store).forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });

    getQuizEvents(this.store).forEach(event => {
      defineEvent(event.eventName, event.aliasEvents, event.mapFn);
    });
  }

  private initProcedures() {
    getHumanProcedures(Human, this.webGLInfo).forEach(proc => {
      defineProcedure(proc);
    });

    getSceneProcedures(
      this.store,
      this.engineService,
      this.paintService,
      this.cameraService,
      this.graphService,
      this.animationService,
      this.viewService,
      this.visibilityLayersService
    ).forEach(proc => {
      defineProcedure(proc);
    });
    
    getCameraProcedures(this.store, this.cameraService).forEach(proc => {
      defineProcedure(proc);
    });

    getTimelineProcedures(this.store, this.actions, this.animationService, this.viewService).forEach(proc => {
      defineProcedure(proc);
    });

    getInputProcedures(this.store).forEach(proc => {
      defineProcedure(proc);
    });

    getUIProcedures(Human, this.store, this.actions, this.engineService).forEach(proc => {
      defineProcedure(proc);
    });

    getLabelsProcedures(Human, this.store, this.labelsService, this.engineService).forEach(proc => {
      defineProcedure(proc);
    });

    getQuizProcedures(this.store, this.quizService).forEach(proc => {
      defineProcedure(proc);
    });
  }
}
